import React, { useEffect, useState, useMemo, useRef, memo } from "react";
import { Link } from "react-router-dom";
import axios from 'axios';
import { MapPin, CalendarBlank, CaretRight, MagnifyingGlass } from "@phosphor-icons/react";

const IMG_BASE = "https://warfc.com/api";

/* ======================================================
    HELPERS
====================================================== */
const getFighterImage = (f) => {
  const rawPath = f?.image_large || f?.image_url || f?.image;
  if (!rawPath) {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(f?.name || "Fighter")}&background=1f2937&color=d4af37`;
  }
  const clean = rawPath.startsWith("/") ? rawPath.slice(1) : rawPath;
  return rawPath.startsWith("http") ? rawPath : `${IMG_BASE}/${clean}`;
};

const getTimestamp = (f) => {
  const date = f.created_at || f.registered_at || f.registration_date || f.createdAt;
  return date ? new Date(date).getTime() : 0;
};

/* ======================================================
    SUB-COMPONENTS (Memoized)
====================================================== */

const Sponsors = memo(() => {
  const logos = [
    { name: "Sponsor 1", url: "https://i.ibb.co/9kpL8Fn0/koz.png" },
    { name: "Sponsor 2", url: "https://i.ibb.co/wNr3rNC8/war11.png" },
    { name: "Sponsor 3", url: "https://i.ibb.co/9kpL8Fn0/koz.png" },
    { name: "Sponsor 4", url: "https://i.ibb.co/wNr3rNC8/war11.png" },
    { name: "Sponsor 5", url: "https://i.ibb.co/9kpL8Fn0/koz.png" },
  ];

  return (
    <div className="bg-black border-b border-white/5 overflow-hidden w-full">
      <div className="flex justify-center pt-3 pb-1">
        <span className="text-[8px] font-black uppercase tracking-[0.4em] text-white/20">Official Partners</span>
      </div>
      <div className="relative flex overflow-hidden py-4">
        {/* The marquee container */}
        <div className="flex animate-marquee whitespace-nowrap gap-24 items-center">
          {[...logos, ...logos, ...logos].map((l, i) => (
            <img 
              key={i} 
              src={l.url} 
              alt={l.name} 
              /* Removed grayscale and opacity-40. Added brightness-110 to make them pop. */
              className="h-8 md:h-10 w-auto object-contain brightness-110 hover:scale-110 transition-all duration-300" 
            />
          ))}
        </div>
      </div>
    </div>
  );
});

const FighterStats = memo(({ count }) => {
  const [displayCount, setDisplayCount] = useState(0);

  useEffect(() => {
    if (!count) return;
    let start = 0;
    const duration = 1500;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutQuad = 1 - (1 - progress) * (1 - progress);
      setDisplayCount(Math.floor(easeOutQuad * count));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [count]);

  return (
    <div className="absolute bottom-10 right-6 md:right-12 z-20 animate-fade-in-up">
  <div className="p-4 md:p-6 rounded-2xl flex flex-col items-center min-w-[140px]">
    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-yellow-500 mb-1">
      FIGHTERS
    </span>

    <div className="flex items-baseline gap-1">
      <h2 className="text-4xl md:text-5xl font-['Oswald'] font-black italic text-white leading-none">
        {displayCount}
      </h2>

      <span className="text-yellow-500 text-xl font-black">
        +
      </span>
    </div>
  </div>
</div>

  );
});

const ChampionsGrid = memo(({ fightersGrouped }) => {
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [startX, setStartX] = useState(0);
  const [sLeft, setSLeft] = useState(0);

  const champions = useMemo(() => {
    const list = [];
    Object.values(fightersGrouped).forEach(group => {
      const champ = group.A?.[0] || group.B?.[0] || group.C?.[0];
      if (champ && (Number(champ.rank) === 1)) {
        list.push({ ...champ, displayWeight: champ.weightclass_name || "N/A" });
      }
    });
    return [...list, ...list]; // Doubled for infinite effect
  }, [fightersGrouped]);

  useEffect(() => {
    const slider = scrollRef.current;
    if (!slider || isDragging || isHovered || champions.length === 0) return;

    let frameId;
    const scrollSpeed = 0.8; 

    const step = () => {
      if (slider) {
        slider.scrollLeft += scrollSpeed;
        if (slider.scrollLeft >= slider.scrollWidth / 2) {
          slider.scrollLeft = 0;
        }
        frameId = requestAnimationFrame(step);
      }
    };
    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [isDragging, isHovered, champions]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setSLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; 
    scrollRef.current.scrollLeft = sLeft - walk;
  };

  if (!champions.length) return null;

  return (
    <section className="mb-24">
      <div className="text-center mb-10">
        <h2 className="font-['Oswald'] text-5xl font-black italic uppercase">The <span className="text-yellow-500">Champions</span></h2>
        <div className="h-1 w-20 bg-yellow-500 mx-auto mt-4" />
      </div>
      <div className="relative" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <div 
          ref={scrollRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={() => setIsDragging(false)}
          onMouseLeave={() => setIsDragging(false)}
          className={`flex gap-6 overflow-x-auto py-10 px-6 no-scrollbar ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{ WebkitOverflowScrolling: 'touch', scrollSnapType: (isDragging || !isHovered) ? 'none' : 'x mandatory' }}
        >
          {champions.map((f, i) => (
            <div key={`${f.fighter_id}-${i}`} className="flex-shrink-0">
              <Link to={`/fighters/${f.fighter_id}`} className="block w-[220px] md:w-[260px]" onClick={(e) => isDragging && e.preventDefault()}>
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden border border-white/10 bg-[#111] transition-all duration-500 hover:border-yellow-500 hover:-translate-y-2">
                  <img src={getFighterImage(f)} alt={f.name} className="w-full h-full object-cover pointer-events-none" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                  <div className="absolute top-0 left-4 bg-yellow-500 text-black px-3 py-1 rounded-b text-[9px] font-black uppercase tracking-tight">{f.displayWeight}</div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="text-[10px] text-yellow-500 font-bold uppercase tracking-widest mb-1 block">{f.gender}</span>
                    <h3 className="text-lg font-black uppercase leading-tight">{f.name} <span className="block text-xs text-gray-400 font-medium">{f.surname}</span></h3>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
      </div>
    </section>
  );
});

const FighterCard = memo(({ f }) => {
  const timestamp = getTimestamp(f);
  const isNew = Date.now() - timestamp < 1000 * 60 * 60 * 24 * 30;

  return (
    <Link to={`/fighters/${f.fighter_id}`} className="group">
      <article className="bg-[#111] rounded-xl border border-gray-800 px-4 py-4 flex items-center gap-4 transition-all hover:border-yellow-500/50 hover:bg-[#161616] hover:translate-x-1">
        <div className="relative w-14 h-14 flex-shrink-0">
          <img src={getFighterImage(f)} alt={f.name} className="w-full h-full object-cover rounded-full border-2 border-gray-800 group-hover:border-yellow-500" loading="lazy" />
          {isNew && <div className="absolute -top-1 -right-1 bg-yellow-500 text-black text-[8px] font-black px-1.5 py-0.5 rounded shadow-lg">NEW</div>}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold uppercase text-sm leading-tight truncate text-white">{f.name} <span className="text-gray-500 font-medium">{f.surname}</span></h3>
          <p className="text-gray-500 text-[10px] uppercase tracking-widest truncate">{f.club_name || "Independent"}</p>
        </div>
        <div className="text-right text-[11px] font-black">
          <span className="text-green-400">{f.wins || 0}W</span>
          <span className="text-gray-700 mx-1">/</span>
          <span className="text-red-400">{f.losses || 0}L</span>
        </div>
      </article>
    </Link>
  );
});

/* ======================================================
    MAIN HOME
====================================================== */
export default function Home() {
  const [homeCover, setHomeCover] = useState(
  "https://i.ibb.co/KpwdjNMj/warr.jpg"
);

useEffect(() => {
  const loadHomeCover = async () => {
    try {
      const response = await fetch(
        "https://warfc.com/api/home-cover"
      );

      const data = await response.json();

      if (data.success && data.imageUrl) {
        setHomeCover(
          `https://warfc.com${data.imageUrl}`
        );
      }
    } catch (error) {
      console.error(
        "Failed to load homepage cover:",
        error
      );
    }
  };

  loadHomeCover();
}, []);

  const [data, setData] = useState({ fighters: [], rankings: [], event: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    (async () => {
      try {
        const [mR, fR, yR, fts, evts] = await Promise.all([
          fetch(`${IMG_BASE}/rankings?category=senior&gender=male`).then(r => r.json()),
          fetch(`${IMG_BASE}/rankings?category=senior&gender=female`).then(r => r.json()),
          fetch(`${IMG_BASE}/rankings?category=youth`).then(r => r.json()),
          fetch(`${IMG_BASE}/fighters`).then(r => r.json()),
          axios.get(`${IMG_BASE}/events`).then(r => r.data.events || r.data)
        ]);

        const upcoming = Array.isArray(evts) ? evts
          .filter(e => e.status === 'published' && new Date(e.date) >= new Date())
          .sort((a, b) => new Date(a.date) - new Date(b.date))[0] : null;

        setData({
          fighters: Array.isArray(fts) ? fts : [],
          rankings: [
            ...(Array.isArray(mR) ? mR.map(x => ({ ...x, gender: "male" })) : []),
            ...(Array.isArray(fR) ? fR.map(x => ({ ...x, gender: "female" })) : []),
            ...(Array.isArray(yR) ? yR.map(x => ({ ...x, gender: x.gender || "youth" })) : []),
          ],
          event: upcoming
        });
      } catch (e) { console.error(e); } finally { setLoading(false); }
    })();
  }, []);

  const rankingsGrouped = useMemo(() => {
    const map = {};
    data.rankings.forEach(f => {
      const key = `${f.gender}-${f.weightclass_name || "N/A"}`;
      const cls = f.class_division || "C";
      map[key] ??= { A: [], B: [], C: [] };
      map[key][cls].push(f);
    });
    return map;
  }, [data.rankings]);

  const filteredFighters = useMemo(() => {
    const term = debouncedSearch.toLowerCase();
    return data.fighters
      .filter(f => !term || `${f.name} ${f.surname}`.toLowerCase().includes(term))
      .sort((a, b) => getTimestamp(b) - getTimestamp(a))
      .slice(0, 12);
  }, [data.fighters, debouncedSearch]);

  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-yellow-500 font-black animate-pulse uppercase tracking-[0.5em]">WARFC</div>;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Sponsors />
      
<section className="relative h-[60vw] min-h-[260px] max-h-[70vh] sm:h-[55vh] lg:h-[70vh] flex items-start justify-center overflow-hidden">
  <div className="absolute inset-0 z-0">
  <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-[#0a0a0a]" />

  <img
    src={homeCover}
    className="w-full h-full object-cover object-center animate-subtle-zoom"
    alt="Hero"
  />
</div>

  {/* FIXED POSITION: Floating inside Hero */}
  <FighterStats count={data.fighters.length} />
</section>

      {data.event && (
        <section className="max-w-7xl mx-auto px-6 mb-24">
          <Link to={`/events/${data.event.id}/fighters`} className="group block relative overflow-hidden rounded-2xl border border-white/10 bg-[#111] hover:border-cyan-500/50 transition-colors duration-500">
            <div className="flex flex-col md:flex-row items-center">
              <div className="w-full md:w-64 h-48 relative overflow-hidden">
                <img src={data.event.image_url ? `${IMG_BASE}/${data.event.image_url.replace(/^\//,'')}` : "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=2000"} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" alt="Event" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
              </div>
              <div className="p-8 flex-1 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="h-2 w-2 rounded-full bg-cyan-500 animate-pulse" />
                    <span className="text-[10px] font-black tracking-widest text-cyan-500 uppercase">Next Live Event</span>
                  </div>
                  <h2 className="font-['Oswald'] text-3xl md:text-4xl font-black italic uppercase group-hover:text-cyan-400 transition-colors">{data.event.name}</h2>
                  <div className="flex gap-4 mt-4">
                    <div className="flex items-center gap-2 text-white/60 text-xs font-bold uppercase"><CalendarBlank size={16} className="text-cyan-500" /> {new Date(data.event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    <div className="flex items-center gap-2 text-white/60 text-xs font-bold uppercase"><MapPin size={16} className="text-red-500" /> {data.event.location}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-cyan-600 text-white px-8 py-3 rounded-lg font-black text-xs uppercase group-hover:bg-white group-hover:text-black transition-all">View Card <CaretRight weight="bold" /></div>
              </div>
            </div>
          </Link>
        </section>
      )}

      <main className="max-w-7xl mx-auto px-6 pb-32">
        <ChampionsGrid fightersGrouped={rankingsGrouped} />

        <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12 border-t border-white/5 pt-12">
          <h2 className="font-['Oswald'] text-4xl font-black italic uppercase">New Warriors</h2>
          <div className="relative w-full md:w-80">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
            <input className="w-full bg-[#111] border border-gray-800 pl-12 pr-6 py-3 rounded-full text-xs uppercase focus:border-yellow-500 outline-none transition-colors" placeholder="Search warrior..." onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFighters.map(f => <FighterCard key={`${f.fighter_id}-${f.gender}`} f={f} />)}
        </div>
      </main>

      <style>{`
  .no-scrollbar::-webkit-scrollbar { display: none; }
  .no-scrollbar { 
    -ms-overflow-style: none; 
    scrollbar-width: none; 
    /* Important: auto-scroll needs 'auto' or 'scroll', not 'smooth' while animating */
    scroll-behavior: auto; 
  }
  
  @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-33.33%); } }
  .animate-marquee { animation: marquee 30s linear infinite; will-change: transform; }

  @keyframes subtle-zoom { 0% { transform: scale(1); } 100% { transform: scale(1.08); } }
  .animate-subtle-zoom { animation: subtle-zoom 20s infinite alternate ease-in-out; }
`}</style>    </div>
  );
}